import type { QueryKey } from "@tanstack/react-query";
import { _ } from "i18n";
import type { Keys } from "i18n/locales/types";
import type { ProviderOperationsAction, ProviderOperationsSearchRowResponse } from "libs/apiClient";
import { isGameScopedAction } from "./isGameScopedAction";
import { PROVIDER_OPERATIONS_ACTION_LABEL_KEYS } from "./providerOperationsActionLabelKeys";
import { useIsGameScopedActionRunning, useProviderOperationsAction } from "./useProviderOperationsAction";
import { useIsBulkActionRunning } from "./useProviderOperationsBulkAction";

type ProviderOperationsActionCellProps = {
	row: ProviderOperationsSearchRowResponse;
	searchQueryKey: QueryKey;
};

export function ProviderOperationsActionCell({ row, searchQueryKey }: ProviderOperationsActionCellProps) {
	const providerOperationsAction = useProviderOperationsAction(searchQueryKey, row.gameId);
	const isGameScopedActionRunning = useIsGameScopedActionRunning(row.gameId);
	const isBulkActionRunning = useIsBulkActionRunning();
	const runningAction = providerOperationsAction.isLoading ? providerOperationsAction.variables?.body.action : undefined;

	const isActionDisabled = (action: ProviderOperationsAction): boolean =>
		isBulkActionRunning ||
		providerOperationsAction.isLoading ||
		(isGameScopedAction(action) && isGameScopedActionRunning);

	if (row.availableActions.length === 0) {
		return <span className="provider-operations-muted">{_("providerOperations.action.none" as keyof Keys)}</span>;
	}

	return (
		<div className="provider-operations-actions">
			{row.availableActions.map((action) => (
				<button
					key={action}
					className="admin-button admin-button--primary admin-button--xs"
					type="button"
					disabled={isActionDisabled(action)}
					onClick={() => providerOperationsAction.mutate({ id: row.id, body: { provider: row.provider, action } })}
				>
					{action === runningAction
						? _("providerOperations.action.running" as keyof Keys)
						: _(PROVIDER_OPERATIONS_ACTION_LABEL_KEYS[action] as keyof Keys)}
				</button>
			))}
		</div>
	);
}
